import React from 'react'
import { Box, Grid, Card, CardContent, Typography, Avatar, SxProps, Theme } from '@mui/material'
import { cardAnimations, createStaggerAnimation } from '../../utils/mobileAnimations'

export interface MobileCardGridItem {
  id: string
  title: string
  icon: React.ReactNode
  value?: string | number
  subtitle?: string
  color?: string
  gradient?: string
  onClick?: () => void
  badge?: string | number
}

interface MobileCardGridProps {
  items: MobileCardGridItem[]
  columns?: 2 | 3 | 4
  spacing?: number
  animated?: boolean
  sx?: SxProps<Theme>
}

/**
 * Mobile Card Grid Component
 * Displays a grid of icon-based navigation or metric cards
 * Optimized for touch interactions
 */
export const MobileCardGrid: React.FC<MobileCardGridProps> = ({
  items,
  columns = 2,
  spacing = 2,
  animated = true,
  sx,
}) => {
  return (
    <Grid container spacing={spacing} sx={sx}>
      {items.map((item, index) => (
        <Grid item xs={12 / columns} key={item.id}>
          <Card
            onClick={item.onClick}
            sx={{
              height: '100%',
              minHeight: 120,
              cursor: item.onClick ? 'pointer' : 'default',
              background: item.gradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: '8px',
              position: 'relative',
              overflow: 'visible',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              ...(animated && createStaggerAnimation(index, 50)),
              '&:active': {
                transform: 'scale(0.98)',
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                p: 2,
                position: 'relative',
              }}
            >
              {/* Badge */}
              {item.badge && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    borderRadius: '8px',
                    px: 1,
                    py: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    minWidth: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {item.badge}
                </Box>
              )}

              {/* Icon */}
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  mb: 1.5,
                  '& .MuiSvgIcon-root': {
                    fontSize: 28,
                  },
                }}
              >
                {item.icon}
              </Avatar>

              {/* Title */}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  mb: item.value || item.subtitle ? 0.5 : 0,
                  lineHeight: 1.2,
                }}
              >
                {item.title}
              </Typography>

              {/* Value */}
              {item.value && (
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    lineHeight: 1,
                  }}
                >
                  {item.value}
                </Typography>
              )}

              {/* Subtitle */}
              {item.subtitle && (
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.9,
                    fontSize: '0.75rem',
                    mt: 0.5,
                  }}
                >
                  {item.subtitle}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  )
}
